import { defineComponent as _defineComponent } from 'vue'
import { ref, watch } from 'vue';
import { uniqueId } from 'lodash';

import { faLightbulbOn } from '@icons/duotone/faLightbulbOn';
import { faWindows } from '@fortawesome/free-brands-svg-icons/faWindows';
import { faApple } from '@fortawesome/free-brands-svg-icons/faApple';
import { faTriangleExclamation } from '@icons/duotone/faTriangleExclamation';
import { faCircleInfo } from '@icons/duotone/faCircleInfo';
import { marked, TokensList } from 'marked';

/**
 * ...
 */
export type TokenCallback = (token: Token) => Token;
/**
 * ...
 */
export interface Props {
  tokens: string | Token[];
  callback?: TokenCallback;
}

interface HeaderToken {
  type: 'text';
  raw: string;
  text: string;
}

interface Header {
  text: string;
  tokens: HeaderToken[];
}
interface Row {
  text: string;
  token: any[];
}

interface TabHolder {
  text: string;
  htmlParsed: string;
  tabs: Tab[];
}

interface Tab {
  title: string;
  tabParsed: string;
}

/**
 * ...
 */
/**
 * ...
 */
export interface Token {
  type:
    | 'heading'
    | 'paragraph'
    | 'list'
    | 'hint-warning'
    | 'hint-info'
    | 'hint-danger'
    | 'tabs'
    | 'table';
  text: string;
  raw: string;
  depth?: number;
  classList?: string[];
  style?: Partial<CSSStyleDeclaration>;
  header?: Header;
  rows?: Row[];
  htmlParsed?: string;
  tabs?: Tab[];
}

interface KeyedToken extends Token {
  key: string;
}

// region Helper Functions

export default /*#__PURE__*/_defineComponent({
  __name: 'SiteDocumentV2',
  props: {
    tokens: null,
    callback: null
  },
  setup(__props: any) {

const props = __props as {
  tokens: string | Token[];
  callback?: TokenCallback;
};



const loading = ref(false);

const items = ref([] as KeyedToken[]);

watch(
  () => props.tokens,
  async (value) => {
    let list: KeyedToken[] = [];

    if (typeof value === 'string' && value.startsWith('https://')) {
      const pageData = await (await fetch(value)).text();
      list = await returnTokensFromMarkdown(pageData);
    } else if (typeof value === `string`) {
      loading.value = true;
      list = await returnTokensFromMarkdown(value);
      loading.value = false;
    } else {
      list = value.map((item) => ({ key: uniqueId(), ...item }));
    }
    items.value = list;
  },
  { immediate: true },
);

async function returnTokensFromMarkdown(markdown: string) {
  const tokens = marked.lexer(markdown);
  console.log(tokens);
  return await returnSectionsFromTokens(tokens);
}

const TAB_REGEX = /\{%\s*tab\s*title\s*=\s*"[^"]*"\s*%\}/g;
const TAB_TITLE_REGEX = /\btitle\s*=\s*"([^"]*)"/;

async function returnSectionsFromTokens(tokens: TokensList) {
  const sections: KeyedToken[] = [];
  const hintObjectHolder = {
    type: '' as 'hint-info' | 'hint-warning' | 'hint-danger',
    text: '',
  };

  const tabHolder: TabHolder = {
    text: '',
    htmlParsed: '',
    tabs: [],
  };

  let tabCount = 0;
  for (const token of tokens) {
    /* 
    Styling Color Replacements for our theme
    */
    // Replace color blue styling with the primary color
    token.raw = token.raw.replace(/color:blue/g, 'color:var(--primary)');

    /* 
    Local Page Replacements
    */
    token.raw = token.raw.replace(
      'supported-controllers.md',
      '/supported-controllers',
    );

    let section: Token = {
      type: 'paragraph',
      text: '',
      raw: '',
    };

    if (token.type === 'paragraph') {
      // We're in a tab group
      if (!token.raw.search(/{% tabs %}/)) {
        console.log('started tabs');
        let replaceThis = token.raw.replace(/{% tabs %}/gs, '');

        if (token.raw.includes('{% tab title')) {
          tabCount++;
          console.log('in tab title');
          //grab the title
          const grabTitle = TAB_TITLE_REGEX.exec(token.raw);
          // replace the template text
          replaceThis = replaceThis.replace(TAB_REGEX, '');
          console.log('pushing tab');
          tabHolder.tabs.push({
            title: grabTitle ? grabTitle[1] : 'Title',
            tabParsed: await marked.parse(replaceThis),
          });
          continue;
        }

        tabHolder.text = await marked.parse(replaceThis);
      }

      if (token.raw.includes('{% tab title')) {
        let replaceThis = token.raw;
        tabCount++;
        console.log('in tab title');
        //grab the title
        const grabTitle = TAB_TITLE_REGEX.exec(token.raw);
        // replace the template text
        replaceThis = replaceThis.replace(TAB_REGEX, '');
        console.log('pushing tab');
        tabHolder.tabs.push({
          title: grabTitle ? grabTitle[1] : 'Title',
          tabParsed: await marked.parse(replaceThis),
        });
        continue;
      }

      if (token.raw.includes('{% endtab %}')) {
        console.log('in end tab');
        tabHolder.tabs[tabCount - 1].tabParsed += await marked.parse(
          token.raw.replace('{% endtab %}', ''),
        );
        continue;
      }

      if (token.raw.includes('{% endtabs %}')) {
        console.log('in end tabs');
        // const replaceThis = token.raw.replace('{% endtabs %}', '');
        // tabHolder.text += await marked.parse(replaceThis);
        sections.push({
          key: uniqueId(),
          type: 'tabs',
          tabs: tabHolder.tabs,
          text: tabHolder.text,
          raw: tabHolder.htmlParsed,
        });
        tabHolder.text = '';
        tabHolder.tabs = [];
        tabHolder.htmlParsed = '';
        tabCount = 0;
        continue;
      }

      // Hint Warning Started
      if (!token.raw.search(/{% hint style="warning" %}/)) {
        hintObjectHolder.type = 'hint-warning';
        let replaceThis = token.raw.replace(/{% hint style="warning" %}/gs, '');

        if (token.raw.includes('{% endhint %}')) {
          replaceThis = replaceThis.replace('{% endhint %}', '');
          sections.push({
            key: uniqueId(),
            type: 'hint-warning',
            text: await marked.parse(replaceThis),
            raw: replaceThis,
            classList: ['hint-warning'],
          });
          continue;
        }
        hintObjectHolder.text = await marked.parse(replaceThis);
        //Hint Info Started
      } else if (!token.raw.search(/{% hint style="info" %}/)) {
        hintObjectHolder.type = 'hint-info';
        let replaceThis = token.raw.replace(/{% hint style="info" %}/gs, '');

        if (token.raw.includes('{% endhint %}')) {
          replaceThis = replaceThis.replace('{% endhint %}', '');
          sections.push({
            key: uniqueId(),
            type: 'hint-info',
            text: await marked.parse(replaceThis),
            raw: replaceThis,
            classList: ['hint-info'],
          });
          continue;
        }
        hintObjectHolder.text = await marked.parse(replaceThis);
        //Hint Danger Started
      } else if (!token.raw.search(/{% hint style="danger" %}/)) {
        hintObjectHolder.type = 'hint-danger';
        let replaceThis = token.raw.replace(/{% hint style="danger" %}/gs, '');

        //TODO Eric replace mark tags in here

        // if (replaceThis.includes(`color:red;`))
        //   replaceThis = replaceThis.replace('color:red;', 'color:#770000;');

        if (token.raw.includes('{% endhint %}')) {
          replaceThis = replaceThis.replace('{% endhint %}', '');
          // console.log(await marked.parse(replaceThis));

          sections.push({
            key: uniqueId(),
            type: 'hint-danger',
            text: await marked.parse(replaceThis),
            raw: replaceThis,
            classList: ['hint-danger'],
          });

          continue;
        }

        hintObjectHolder.text = await marked.parse(replaceThis);
        // Closed Hint
      } else if (token.raw.includes('{% endhint %}')) {
        const replaceThis = token.raw.replace('{% endhint %}', '');
        hintObjectHolder.text += await marked.parse(replaceThis);

        section = {
          type: hintObjectHolder.type,
          text: hintObjectHolder.text,
          raw: hintObjectHolder.text,
          classList: [hintObjectHolder.type],
        };

        //   hintOBject.text = '';
      } else {
        section = {
          type: 'paragraph',
          text: await marked.parse(token.raw),
          raw: token.raw,
          classList: [],
        };
      }
    }

    if (token.type === 'table') {
      console.log(token.raw);
      if (token.raw.includes('{% tab title')) {
        console.log('[TABLE] has tab');
        const grabTitle = TAB_TITLE_REGEX.exec(token.raw);

        const replaceThis = token.raw.replace(TAB_REGEX, '');
        tabCount++;

        //grab the title

        // replace the template text

        console.log(`${grabTitle[1]} pushing tab [Table]`);
        tabHolder.tabs.push({
          title: grabTitle ? grabTitle[1] : 'Title',
          tabParsed: await marked.parse(replaceThis),
        });
      }
      if (
        token.raw.includes('{% endtab %}') &&
        !token.raw.includes('{% endtabs %}')
      ) {
        console.log('in end tab [TABLE]');
        console.log(tabHolder.tabs);

        console.log(tabCount);
        tabHolder.tabs[tabCount - 1].tabParsed += await marked.parse(
          token.raw.replace('{% endtab %}', ''),
        );
        continue;
      } else if (token.raw.includes('{% endtabs %}')) {
        console.log('in end tabs [TABLE]');
        tabHolder.tabs[tabCount - 1].tabParsed += await marked.parse(
          token.raw.replace('{% endtab %}', '').replace('{% endtabs %}', ''),
        );
        // const replaceThis = token.raw.replace('{% endtabs %}', '');
        // tabHolder.text += await marked.parse(replaceThis);
        sections.push({
          key: uniqueId(),
          type: 'tabs',
          tabs: tabHolder.tabs,
          text: tabHolder.text,
          raw: tabHolder.htmlParsed,
          // classList: ['site-document-tabs'],
        });
        tabCount = 0;
        tabHolder.text = '';
        tabHolder.tabs = [];
        tabHolder.htmlParsed = '';

        continue;
      }

      section = {
        type: 'table',
        htmlParsed: await marked.parse(token.raw),
        raw: token.raw,
        text: token.text,
      };
    }

    if (token.type === 'heading') {
      if (token.depth === 2) {
        if (token.text.startsWith('icon:')) continue;
        section = {
          type: 'heading',
          text: token.text,
          raw: token.raw,
          depth: token.depth,
        };
      }
      if (token.depth === 3) {
        section = {
          type: 'heading',
          text: token.text,
          raw: token.raw,
          depth: token.depth,
        };
      }
      if (token.depth === 4) {
        section = {
          type: 'heading',
          text: token.text,
          raw: token.raw,
          depth: token.depth,
        };
      }
    }
    if (token.type === 'list') {
      if (token.raw.includes('{% endhint %}')) {
        const replaceThis = token.raw.replace('{% endhint %}', '');
        hintObjectHolder.text += await marked.parse(replaceThis);

        section = {
          type: hintObjectHolder.type,
          text: hintObjectHolder.text,
          raw: hintObjectHolder.text,
          classList: [hintObjectHolder.type],
        };
      } else {
        section = {
          type: 'list',
          raw: token.raw,
          text: await marked.parse(token.raw),
        };
      }
    }

    if (props.callback) {
      section = props.callback(section);
    }

    sections.push({
      key: uniqueId(),
      ...section,
    });
  }
  console.log(sections);
  return sections;
}

// endregion Helper Functions

return { __sfc: true,props, loading, items, returnTokensFromMarkdown, TAB_REGEX, TAB_TITLE_REGEX, returnSectionsFromTokens, faLightbulbOn, faWindows, faApple, faTriangleExclamation, faCircleInfo }
}

})